import { AxiosResponse } from 'axios';
import Breadcrumbs from 'components/breadcrumbs';
import Error from 'components/error';
import FilterSidebar from 'components/filter-sidebar';
import LoadingSpinner from 'components/loading-spinner';
import RadioSelectItem from 'components/radio-select-item';
import Select from 'components/select';
import Summary from 'components/summary';
import { useAccountState } from 'contexts/account/account-context';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import Mui from 'materials';
import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { generatePath, useHistory, useRouteMatch } from 'react-router';
import { OwnerType } from 'types/shared/owner-type.enum';
import FishGroup from 'views/planning-prognosis/features/sites/components/fish-group';
import ForecastActivities from 'views/planning-prognosis/features/sites/components/forceast-activities';
import SiteFeedPlan from 'views/planning-prognosis/features/sites/components/site-feed-plan';

import { Alert, Button, CircularProgress } from '@mui/material';
import { useGet } from 'hooks/use-get';
import { usePost } from 'hooks/use-post';
import _ from 'underscore';
import { postCalculation } from 'views/planning-prognosis/api/create-calculation';
import { getPlanningPrognosisSite } from 'views/planning-prognosis/api/get-site';
import { getFishGroups } from 'views/planning-prognosis/api/get-site-fish-groups';
import { getPlanAndPrognosisSites } from 'views/planning-prognosis/api/get-sites';
import PlanningPrognosisTabsHeader from '../../components/planning-prognosis-tabs-header';
import { getSiteCalculationStatus } from './api/get-site-calculation-status';

type ParamTypes = {
  localSiteId: string;
};

type Props = {};

const ViewSite: React.FC<Props> = () => {
  const intl = useIntl();
  const history = useHistory();
  const queryClient = useQueryClient();
  const { params, path } = useRouteMatch<ParamTypes>();
  const { account } = useAccountState();
  const snackbarDispatch = useSnackbarDispatch();

  // #region STATE

  const [refetchInterval, setRefetchInterval] = useState<number | false | undefined>(false);
  const previousRecalculating = useRef<boolean | undefined>();

  // #endregion STATE

  // #region FETCH DATA

  const {
    data: site,
    error,
    isError,
    isLoading,
  } = useGet(
    ['getPlanningPrognosisSite', account.company?.companyId, params.localSiteId],
    ({ queryKey }) => getPlanningPrognosisSite(String(queryKey[1]), String(queryKey[2])),
    {
      enabled: !!account.company?.companyId,
    }
  );

  const { data: sites } = useGet(
    ['getPlanAndPrognosisSites', account.company?.companyId],
    ({ queryKey }) => getPlanAndPrognosisSites(String(queryKey[1])),
    {
      enabled: !!account.company?.companyId,
    }
  );

  const { data: calculationStatus } = useGet(
    ['getSiteCalculationStatus', account.company?.companyId, params.localSiteId],
    ({ queryKey }) => getSiteCalculationStatus(String(queryKey[1]), String(queryKey[2])),
    {
      enabled: !!account.company?.companyId && !!params.localSiteId,
      refetchInterval: refetchInterval,
    }
  );

  useEffect(() => {
    if (!calculationStatus) return;

    if (previousRecalculating.current === true && !calculationStatus.recalculating) {
      [['getPlanningPrognosisSite'], ['getFishGroups']].forEach((queryKey) => {
        queryClient.invalidateQueries({ queryKey });
      });
    }

    previousRecalculating.current = calculationStatus.recalculating;
    setRefetchInterval(calculationStatus.recalculating ? 3000 : false);
  }, [calculationStatus, queryClient]);

  const { data: fishGroups } = useGet(['getFishGroups', site?.globalSiteId], ({ queryKey }) => getFishGroups(String(queryKey[1])), {
    enabled: !!site?.globalSiteId,
  });

  // #endregion FETCH DATA

  // #region POST DATA

  const startCalculation = usePost<AxiosResponse, { owner: OwnerType; id: string }>(
    (request) => postCalculation(request.owner, request.id),
    () => {
      queryClient.invalidateQueries({
        queryKey: ['getSiteCalculationStatus'],
      });
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );

  // #endregion POST DATA

  // #region HANDLERS

  const handleChangeSite = (value: string) => {
    Object.assign(params, { localSiteId: value });
    history.push(generatePath(path, params));
  };

  // #endregion HANDLERS

  return isError ? (
    <Error error={error} />
  ) : !site || isLoading ? (
    <LoadingSpinner />
  ) : (
    <>
      <PlanningPrognosisTabsHeader
        //TODO: add the specific breadcrumbs
        utils={
          <Breadcrumbs
            crumbs={[
              {
                text: site.siteName,
                url: site.siteId,
              },
            ]}
          />
        }
      />

      <FilterSidebar>
        <Select
          label={intl.formatMessage({ id: `planningPrognosis.navigation.listSites` })}
          value={site.siteId}
          onChange={(event) => handleChangeSite(String(event.target.value))}>
          {_.chain(sites)
            .filter((s) => s.activeFishGroups !== 0)
            .filter((s) => s.hasEwosProfiles)
            .sortBy((s) => s.siteName.toLowerCase())
            .sortBy((s) => s.isMapped && s.isUserAuthorized)
            .value()
            .map((site) => (
              <RadioSelectItem key={site.siteId} value={site.siteId} selected={site.siteId === site.siteId}>
                {site.siteName}
              </RadioSelectItem>
            ))}
        </Select>
      </FilterSidebar>
      {calculationStatus?.recalculating && (
        <Alert severity="success" icon={<CircularProgress size={18} />}>
          <Mui.AlertTitle>
            <FormattedMessage id="planningPrognosis.alert.title" />
          </Mui.AlertTitle>
          <FormattedMessage id="planningPrognosis.alert.info" />
        </Alert>
      )}
      {!calculationStatus?.recalculating && !!fishGroups
        ? fishGroups.some((fg) => fg.recalculationNeeded) &&
          account.company?.features.hasPlanningPrognosis && (
            <Alert
              severity="warning"
              action={
                <Button
                  variant="outlined"
                  disabled={startCalculation.isPending || calculationStatus?.recalculating}
                  onClick={() => startCalculation.mutate({ owner: OwnerType.SITE, id: site.siteId })}>
                  <FormattedMessage id={`general.recalculate`} />
                </Button>
              }
              sx={{ alignItems: 'center' }}>
              <FormattedMessage id={`general.recalculateIsNeeded`} />
            </Alert>
          )
        : null}
      <Summary
        items={[
          {
            text: intl.formatMessage({ id: 'planningPrognosis.listSites.list.activeFishGroupsLabel' }),
            header: <FormattedNumber value={site.activeFishGroups} minimumFractionDigits={0} maximumFractionDigits={0} />,
          },
          {
            text: intl.formatMessage({ id: 'planningPrognosis.listSites.list.biomassLabel' }),
            header: <FormattedNumber value={site.totalBiomass} minimumFractionDigits={0} maximumFractionDigits={0} />,
          },
          {
            text: intl.formatMessage({ id: 'planningPrognosis.listSites.list.noFishLabel' }),
            header: <FormattedNumber value={site.totalNumber} />,
          },
          {
            text: intl.formatMessage({ id: 'planningPrognosis.listSites.list.avgWeightLabel' }),
            header: (
              <FormattedNumber
                value={(site.totalBiomass / site.totalNumber) * 1000}
                minimumFractionDigits={0}
                maximumFractionDigits={0}
              />
            ),
          },
        ]}
      />
      <FishGroup localSiteId={site.siteId} globalSiteId={site.globalSiteId} />
      <ForecastActivities globalSiteId={site.globalSiteId} />
      <SiteFeedPlan siteName={site.siteName} globalSiteId={site.globalSiteId} />
    </>
  );
};

export default ViewSite;
